// pages/admin/AdminUsers.jsx
import { useAuth } from '../../utils/auth';
import { useState, useEffect } from 'react';
import { auth as authApi, rooms as roomsApi, gmSkills as gmSkillsApi } from '../../utils/api';
import { Plus, Edit2, Award } from 'lucide-react';
import toast from 'react-hot-toast';

const ROLES = ['admin','manager','staff','gm','freelance'];
const ROLE_COLORS = { admin:'badge-red', manager:'badge-amber', staff:'badge-purple', gm:'badge-teal', freelance:'badge-gray' };

function SkillsModal({ user, rooms, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [loading, setLoading]   = useState(false);

  useEffect(() => {
    gmSkillsApi.byUser(user.id).then(r => setSelected(r.data.skills.map(s => s.room_id)));
  }, [user.id]);

  const toggle = (roomId) =>
    setSelected(prev => prev.includes(roomId) ? prev.filter(id => id !== roomId) : [...prev, roomId]);

  const save = async () => {
    setLoading(true);
    try {
      await gmSkillsApi.setUserSkills(user.id, { roomIds: selected });
      toast.success('Compétences sauvegardées');
      onSaved(); onClose();
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <h3 className="modal-title">Compétences de {user.first_name} {user.last_name}</h3>
        <p className="text-sm text-muted" style={{ marginBottom:16 }}>
          Sélectionnez les salles que ce collaborateur peut animer
        </p>
        <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
          {rooms.map(room => (
            <div key={room.id} onClick={() => toggle(room.id)} style={{
              display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
              borderRadius:10, cursor:'pointer', transition:'all 0.15s',
              background: selected.includes(room.id) ? 'rgba(0,210,180,0.1)' : 'var(--bg-elevated)',
              border: `1px solid ${selected.includes(room.id) ? 'var(--teal)' : 'var(--border)'}`,
            }}>
              <div style={{
                width:20, height:20, borderRadius:6,
                border:`2px solid ${selected.includes(room.id)?'var(--teal)':'var(--border)'}`,
                background: selected.includes(room.id) ? 'var(--teal)' : 'transparent',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0,
              }}>
                {selected.includes(room.id) && <span style={{color:'#fff',fontSize:12,fontWeight:700}}>✓</span>}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{room.name}</div>
                {room.duration_min && (
                  <div style={{ fontSize:12, color:'var(--text-3)' }}>
                    {room.duration_min} min · {room.min_players}–{room.max_players} joueurs
                  </div>
                )}
              </div>
            </div>
          ))}
          {rooms.length === 0 && <p className="text-muted text-sm">Aucune salle configurée</p>}
        </div>
        <div style={{ fontSize:13, color:'var(--text-3)', marginBottom:16 }}>
          {selected.length} salle(s) sélectionnée(s)
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>
            {loading ? <span className="spinner" style={{width:16,height:16}}/> : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const { can } = useAuth();
  if (!can('admin.users')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [users, setUsers]           = useState([]);
  const [rooms, setRooms]           = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [skillsUser, setSkillsUser] = useState(null);
  const [selected, setSelected]     = useState(null);
  const [form, setForm] = useState({ email:'', password:'', firstName:'', lastName:'', phone:'', role:'staff' });

  const load = () => {
    authApi.users().then(r => setUsers(r.data.users));
    roomsApi.list().then(r => setRooms(r.data.rooms));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (selected) await authApi.updateUser(selected.id, { firstName:form.firstName, lastName:form.lastName, email:form.email, phone:form.phone, role:form.role, isActive:true });
      else await authApi.createUser(form);
      toast.success(selected ? 'Utilisateur mis à jour' : 'Utilisateur créé');
      setShowForm(false); setSelected(null); load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
  };

  const openEdit = (u) => {
    setSelected(u);
    setForm({ email:u.email, password:'', firstName:u.first_name, lastName:u.last_name, phone:u.phone||'', role:u.role });
    setShowForm(true);
  };

  const field = k => ({ value:form[k]||'', onChange:e=>setForm(f=>({...f,[k]:e.target.value})) });

  return (
    <>
      <div className="page-header">
        <div><h2>Utilisateurs</h2><p>Gestion des comptes, droits et compétences GM</p></div>
        <button className="btn btn-primary" onClick={() => {
          setSelected(null);
          setForm({email:'',password:'',firstName:'',lastName:'',phone:'',role:'staff'});
          setShowForm(true);
        }}>
          <Plus size={15}/> Nouvel utilisateur
        </button>
      </div>

      <div className="page-body">
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Nom</th><th>Email</th><th>Téléphone</th><th>Rôle</th><th>Statut</th><th>Salles</th><th></th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td className="bold">{u.first_name} {u.last_name}</td>
                    <td className="text-sm text-muted">{u.email}</td>
                    <td className="text-sm">{u.phone||'—'}</td>
                    <td><span className={`badge ${ROLE_COLORS[u.role]||'badge-gray'}`}>{u.role}</span></td>
                    <td><span className={`badge ${u.is_active?'badge-green':'badge-red'}`}>{u.is_active?'Actif':'Inactif'}</span></td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => setSkillsUser(u)} title="Gérer les compétences">
                        <Award size={13} color="var(--teal)"/> Salles
                      </button>
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}><Edit2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-title" style={{marginBottom:12}}>Hiérarchie des rôles</div>
          <div style={{display:'flex',gap:16,flexWrap:'wrap',marginBottom:12}}>
            {[['admin','Accès total'],['manager','Gestion staff'],['staff','Caisse + résa'],['gm','Sessions'],['freelance','Disponibilités']].map(([role,desc])=>(
              <div key={role} style={{display:'flex',alignItems:'center',gap:8}}>
                <span className={`badge ${ROLE_COLORS[role]}`}>{role}</span>
                <span className="text-sm text-muted">{desc}</span>
              </div>
            ))}
          </div>
          <div style={{padding:'10px 14px',background:'var(--bg-elevated)',borderRadius:8,fontSize:13,color:'var(--text-2)'}}>
            <Award size={12} style={{display:'inline',marginRight:6}} color="var(--teal)"/>
            Le bouton <strong>Salles</strong> définit quelles salles un collaborateur peut animer.
            Sans salle assignée = qualifié pour toutes.
          </div>
        </div>
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowForm(false)}>
          <div className="modal">
            <h3 className="modal-title">{selected?'Modifier':'Nouvel'} utilisateur</h3>
            <form onSubmit={submit}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Prénom *</label><input className="input" required {...field('firstName')}/></div>
                <div className="form-group"><label className="form-label">Nom *</label><input className="input" required {...field('lastName')}/></div>
              </div>
              {!selected && <>
                <div className="form-group"><label className="form-label">Email *</label><input type="email" className="input" required {...field('email')}/></div>
                <div className="form-group"><label className="form-label">Mot de passe (min. 8 car.) *</label><input type="password" className="input" required {...field('password')}/></div>
              </>}
              <div className="form-row">
                <div className="form-group"><label className="form-label">Téléphone</label><input type="tel" className="input" {...field('phone')}/></div>
                <div className="form-group"><label className="form-label">Rôle *</label>
                  <select className="input" required {...field('role')}>
                    {ROLES.map(r=><option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowForm(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{selected?'Mettre à jour':'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {skillsUser && (
        <SkillsModal user={skillsUser} rooms={rooms} onClose={()=>setSkillsUser(null)} onSaved={load}/>
      )}
    </>
  );
}

export default AdminUsers;
